import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QrCode, UserPlus, MapPin, Phone, User } from 'lucide-react';
import { rsvpService, eventsService } from '../services/api-service';
import './RSVPScan.css';

const RSVPScan = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpData, setRsvpData] = useState({
    name: '',
    phone: '',
    startingLocation: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const eventData = await eventsService.getEventById(eventId);
      if (eventData) {
        setEvent(eventData);
      }
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create RSVP
      const rsvpResponse = await rsvpService.createRSVP({
        eventId: eventId,
        name: rsvpData.name,
        phone: rsvpData.phone,
        startingLocation: rsvpData.startingLocation
      });

      // If RSVP created successfully, schedule location reveal
      if (rsvpResponse.success || rsvpResponse.data) {
        const rsvpId = rsvpResponse.data?.id || rsvpResponse.id;
        
        // Calculate travel time and schedule location reveal
        // Note: Event location will be provided by backend
        try {
          await rsvpService.scheduleLocationReveal(rsvpId, {
            startingLocation: rsvpData.startingLocation,
            eventId: eventId,
            // Backend will calculate travel time and schedule notification
          });
        } catch (revealError) {
          console.error('Error scheduling location reveal:', revealError);
          // Don't fail RSVP if reveal scheduling fails - it can be done manually
        }
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error creating RSVP:', error);
      alert('Failed to RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rsvp-scan-page">
        <div className="container">
          <div className="rsvp-scan-loading">
            <QrCode size={64} className="spinning" />
            <h3>Loading event...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="rsvp-scan-page">
        <div className="container">
          <div className="rsvp-scan-error">
            <h2>Event Not Found</h2>
            <p>The event you're trying to RSVP for does not exist.</p>
            <button onClick={() => navigate('/events')} className="btn">
              Browse Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rsvp-scan-page">
        <div className="container">
          <div className="rsvp-scan-success">
            <UserPlus size={64} />
            <h2>RSVP Confirmed!</h2>
            <p>You're all set for {event.title}.</p>
            <div className="rsvp-scan-success-info">
              <h3>What Happens Next?</h3>
              <ul>
                <li>We've calculated your travel time from {rsvpData.startingLocation}</li>
                <li>You'll receive the secret location at the perfect time</li>
                <li>Everyone arrives together, location stays protected</li>
              </ul>
            </div>
            <button onClick={() => navigate('/events')} className="btn btn-orange">
              View More Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-scan-page">
      {/* Header */}
      <section className="rsvp-scan-header">
        <div className="container">
          <div className="rsvp-scan-header-content">
            <QrCode size={48} className="rsvp-scan-header-icon" />
            <h1>RSVP via QR Code</h1>
            <p>You scanned a QR code to RSVP for this event</p>
          </div>
        </div>
      </section>

      {/* Event Info */}
      <section className="rsvp-scan-event-info">
        <div className="container">
          <div className="rsvp-scan-event-card">
            <h2>{event.title}</h2>
            {event.date && (
              <p className="rsvp-scan-event-date">
                {new Date(event.date).toLocaleDateString('en-AU', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
            {event.location && (
              <p className="rsvp-scan-event-location">
                <MapPin size={18} />
                Location will be revealed based on your travel time
              </p>
            )}
          </div>
        </div>
      </section>

      {/* RSVP Form */}
      <section className="rsvp-scan-form-section section">
        <div className="container">
          <div className="rsvp-scan-form-container">
            <h2>Complete Your RSVP</h2>
            <p className="rsvp-scan-form-description">
              Enter your details below. We'll calculate your travel time and send you 
              the location at the perfect time so everyone arrives together.
            </p>

            <form onSubmit={handleSubmit} className="rsvp-scan-form">
              <div className="rsvp-scan-form-group">
                <label htmlFor="rsvp-name">
                  <User size={18} />
                  Name *
                </label>
                <input
                  type="text"
                  id="rsvp-name"
                  value={rsvpData.name}
                  onChange={(e) => setRsvpData({ ...rsvpData, name: e.target.value })}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="rsvp-scan-form-group">
                <label htmlFor="rsvp-phone">
                  <Phone size={18} />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="rsvp-phone"
                  value={rsvpData.phone}
                  onChange={(e) => setRsvpData({ ...rsvpData, phone: e.target.value })}
                  required
                  placeholder="Your phone number"
                />
                <small>We'll send location details via SMS</small>
              </div>

              <div className="rsvp-scan-form-group">
                <label htmlFor="rsvp-location">
                  <MapPin size={18} />
                  Starting Location *
                </label>
                <input
                  type="text"
                  id="rsvp-location"
                  value={rsvpData.startingLocation}
                  onChange={(e) => setRsvpData({ ...rsvpData, startingLocation: e.target.value })}
                  required
                  placeholder="City, suburb, or address (e.g., Brisbane, Caloundra)"
                />
                <small>
                  This helps us calculate your travel time and send the location at the right time.
                  <br />
                  <strong>Furthest people get location first, closest get it last</strong> - so everyone arrives together!
                </small>
              </div>

              <div className="rsvp-scan-form-agreement">
                <label className="rsvp-scan-checkbox">
                  <input type="checkbox" required />
                  <span>
                    I agree to bring my rubbish home and respect the earth, 
                    each other, and the community we're building
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-orange btn-block"
                disabled={submitting}
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <UserPlus size={20} />
                    Confirm RSVP
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RSVPScan;

